import {
  CommonHelperFunctions,
  ComponentType,
  DashboardState,
  EDatasourceType,
  EPropertyType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IComponent,
  IDisplayItem,
  IDisplayItemComponent,
  IDisplayItemProperty,
  IEditability,
  IEvent,
  IFillStyle,
  IKType,
  IKTypeField,
  IKuikaStyle,
  IProperty,
  IPropertyDefinition,
  IScreenDesign,
  IStylingBorder,
  IStylingEffects,
  IStylingPosition,
  IStylingShadow,
  IStylingTypography,
  ISymbol,
  IValidation,
  IVisibility,
  SpecialDIIds,
  StyleHelper
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import {
  IApplyPropertyGridChangesToDisplayParameters,
  IChangeSelectedPropertyTypeParameters
} from "../../models/interface/function-parameters";
import { KMainFunctions } from "./main-functions";
import { PropertyHelper } from "./property-helper";
import { ScreenDesignHelper } from "./screen-design-helper";
import { StaticTableHelper } from "./static-table-helper";

/**
 * Deterministic table-column field-binding symbol fields — the single source of
 * truth shared by the human designer's column picker (`applyTableFieldBinding` in
 * `designer-property-data-editor-actions.ts`) and the AI/MCP datasource-binding
 * path (`enrichTableFieldBindingSymbol` in `designer-property-actions.ts`). Given a
 * datasource field resolved from the enclosing table's action `returnTypeModel`,
 * it stamps the field-of-type binding fields (fieldName / description / fieldType /
 * fixedValue / category) exactly the way the manual cascader does, so both paths
 * emit a byte-identical symbol. Mutates the supplied symbol in place.
 *
 * `fieldToDisplay` and `tableLabelSourceType` are the caller's responsibility: the
 * picker keeps the exact JSON the user selected; the MCP path serializes the
 * resolved field. Keeping them out of here preserves the picker's existing
 * behavior (no reserialization) while letting the MCP enricher set them.
 */
export function buildTableFieldBindingSymbolFields(
  symbol: ISymbol,
  selectedField: IKTypeField,
  prePath?: string
): void {
  let fixedValue: string;
  if (prePath && prePath.length) {
    fixedValue = `[datafield:${prePath}.${selectedField.name}]`;
    symbol.fieldName = `${prePath}.${selectedField.name}`;
    symbol.description = `${prePath}.${selectedField.name}`;
  } else {
    fixedValue = `[datafield:${selectedField.name}]`;
    symbol.fieldName = selectedField.name;
    symbol.description = selectedField.name ?? "";
  }
  symbol.fieldType = selectedField.type;
  symbol.category = ESymbolCategory.actionField;
  symbol.fixedValue = fixedValue.toLowerCase();
}

/**
 * KOA-18226 — where `saveActionToEvent` routed an action attach.
 *
 * A CLOSED vocabulary so telemetry can group by it, and so the four `dropped-*` members
 * are impossible to confuse with the owners that really do store the action. The write is
 * routed by `dashboardState`, which is sticky: nothing resets it except navigating back to
 * the design view, and the one exception (KOA-17362) covers `workFlow` on screen load only.
 * So a designer left on another tool silently discards every screen action-attach for the
 * rest of the session — which is exactly what the `dropped-*` members name.
 */
export type SaveActionOwner =
  /** Landed on the screen component's own event — the only success for a screen attach. */
  | "component"
  | "document-parameters"
  | "scheduled-job"
  | "api-method"
  | "workflow-step"
  | "bluetooth-method"
  /** Discarded: the dashboard said scheduled-jobs but no job was selected. */
  | "dropped-scheduled-jobs-no-job"
  /** Discarded: the dashboard said API management but no method was selected. */
  | "dropped-api-no-method"
  /** Discarded: the dashboard said workflow but no step was selected. */
  | "dropped-workflow-no-step"
  /** Discarded: the dashboard said data diagram but no bluetooth method was selected. */
  | "dropped-data-diagram-no-method"
  /** Discarded: the component was not in the dictionary this writer reads. */
  | "dropped-component-missing";

export interface SaveActionToEventResult {
  readonly owner: SaveActionOwner;
  /**
   * True only when the action is READ BACK from the component's event afterwards.
   * Never inferred from the branch taken — that is the assumption this exists to remove.
   */
  readonly attachedToComponent: boolean;
  /** The routing input, carried so a failure names the state that caused it. */
  readonly dashboardState: DashboardState;
}

export class PropertySdHelper {
  screenContextProviderValueDelegate: () => IScreenDesignContextState;

  propertyHelper: PropertyHelper;

  // Optional: only the provider's main PropertySdHelper instance (created with `this`)
  // needs it, for the StartProcess trigger sync in saveActionToEvent/deleteActionFromEvent.
  // Lightweight instances spun up by property panels for grid-only work pass just the
  // delegate; their trigger-sync paths are guarded below.
  private readonly sdInstance?: import("../../contexts/screen-design-context/screen-design-context-provider").ScreenDesignContextProvider;

  constructor(
    screenContextProviderValueDelegate: () => IScreenDesignContextState,
    sdInstance?: import("../../contexts/screen-design-context/screen-design-context-provider").ScreenDesignContextProvider
  ) {
    this.screenContextProviderValueDelegate = screenContextProviderValueDelegate;
    this.sdInstance = sdInstance;
    this.propertyHelper = new PropertyHelper();
  }

  getScreenDesignContext = (): IScreenDesignContextState => {
    return this.screenContextProviderValueDelegate();
  };

  getSdInstance = () => this.sdInstance;

  createPropertySchema(component: IDisplayItemComponent) {
    const propertyList = component.properties;
    const schema: any = { properties: {} };
    propertyList.forEach((property) => {
      const propertyDefinition = this.getScreenDesignContext().getPropertyDefinition(component, property);
      if (propertyDefinition) {
        schema.properties[propertyDefinition.name] = { type: "string" };
      }
    });
    return schema;
  }

  createPropertySchemaData(component: IDisplayItemComponent) {
    const propertyList = component.properties;
    const schemaData: any = {};
    propertyList.forEach((property) => {
      const propertyDefinition = this.getScreenDesignContext().getPropertyDefinition(component, property);
      if (propertyDefinition) {
        schemaData[propertyDefinition.name] = property.symbol.fixedValue;
      }
    });
    return schemaData;
  }

  changeSelectedPropertyType(params: IChangeSelectedPropertyTypeParameters) {
    if (!this.getScreenDesignContext() || !params.kuikaId) {
      return;
    }
    const sd = this.getScreenDesignContext().getScreenDesign();
    const dict = this.getScreenDesignContext().screenDesignDictionary;
    const di = dict[parseInt(params.kuikaId, 10)];

    if (!di || !di.item) {
      return;
    }
    if (di) {
      let property: IDisplayItemProperty | undefined;
      property = di.item.properties.find((x) => x.name === params.propertyName);
      if (property) {
        property.selectedType = Number(params.selectedValue);
        property.selectedTypeOptions = params.selectedTypeOptions;
        property.symbol.fixedValue = "";
      }
      const sdContext = this.getScreenDesignContext();
      sdContext.setScreenDesign(sd, ESdChangeReason.ChangePropertyType);
    }
  }

  isJson = (item: string) => {
    let value = typeof item !== "string" ? JSON.stringify(item) : item;

    try {
      value = JSON.parse(value);
    } catch (e) {
      return false;
    }

    return typeof value === "object" && value !== null;
  };

  getReturnTypeModelByFields = (fieldName: string, returnTypeModel: IKType): IKTypeField | undefined => {
    if (!fieldName) return undefined;

    const fields: string[] = fieldName.split(".");

    if (fields.length === 0) {
      return undefined;
    }

    if (fields.length === 1) {
      const fieldNameWithoutModifier: string = fieldName.split("[")[0];
      return returnTypeModel?.elements?.find((x) => x.name === fieldNameWithoutModifier);
    }

    let currentModel: IKType = _.clone(returnTypeModel);

    for (const field of fields) {
      const fieldNameWithoutModifier: string = field.split("[")[0];

      const fieldModel = currentModel.elements?.find((x) => x.name === fieldNameWithoutModifier);

      if (fieldModel?.elementTypeModel) {
        currentModel = _.clone(fieldModel.elementTypeModel);
      }
    }

    const lastField: string = fields.at(-1) as string;
    const lastFieldNameWithoutModifier: string = lastField.split("[")[0];

    return currentModel.elements?.find((x) => x.name === lastFieldNameWithoutModifier);
  };

  getActionFieldsBySymbol = (symbol: ISymbol | undefined, includeLists: boolean = false): IKTypeField[] => {
    let result: IKTypeField[] = [];

    if (!symbol) {
      return result;
    }

    const selectedActionId = symbol.actionDefinitionId;
    const selectedActionDefinitionObjectPath = symbol.actionDefinitionObjectPath;
    const sdc = this.getScreenDesignContext();
    let selectedActionDef: IActionDefinitionDto | undefined = sdc.actionDefinitions.find(
      (actionDef) => actionDef.id === selectedActionId
    );
    let returnTypeModel: IKType | undefined = _.cloneDeep(selectedActionDef?.returnTypeModel);

    if (selectedActionDef?.name === "GetFromBag" || selectedActionDef?.name === "FetchFromCache") {
      selectedActionDef = _.cloneDeep(selectedActionDef);
      const actions = CommonHelperFunctions.getAllActions(this.getScreenDesignContext().getScreenDesign().displayItems);
      let selectedAction = actions.find((a) => a.id === symbol.resultActionId);
      if (!selectedAction && selectedActionId) {
        selectedAction = actions.find((a) => a.actionDefinitionId === selectedActionId);
      }
      returnTypeModel = _.cloneDeep(selectedAction?.returnTypeModel) as IKType;
    }

    if (!selectedActionDef && symbol.fieldToDisplay && this.isJson(symbol.fieldToDisplay)) {
      returnTypeModel = JSON.parse(symbol.fieldToDisplay);

      if (returnTypeModel) {
        // TODO: IA: Nasty type casting. Fix this.
        returnTypeModel = this.getReturnTypeModelByFields(symbol.fieldName, returnTypeModel) as unknown as IKType;
      }

      returnTypeModel?.elements?.forEach((element: IKTypeField) => {
        if (includeLists || element.isList !== true) {
          result.push(_.clone(element));
        }
      });

      return result;
    }

    if (returnTypeModel?.elements) {
      if (selectedActionDefinitionObjectPath !== undefined) {
        const pathArr = selectedActionDefinitionObjectPath.split(".");
        let activeModel: any | undefined = _.clone(returnTypeModel);
        if (pathArr?.length > 0) {
          pathArr.forEach((pathItem, index) => {
            if (index > 0) {
              const el = activeModel?.elements?.find((e) => e.name == pathItem);
              if (el?.elementTypeModel) {
                activeModel = _.clone(el?.elementTypeModel);
              } else {
                result = [];
                activeModel = undefined;
              }
            }
            if (index === pathArr?.length - 1) {
              activeModel?.elements.forEach((element: IKTypeField) => {
                if (includeLists || element.isList !== true) {
                  result.push(_.clone(element));
                }
              });
            }
          });
        }
      } else {
        returnTypeModel.elements.forEach((element: IKTypeField) => {
          if (includeLists || element.isList !== true) {
            result.push(_.clone(element));
          }
        });
      }
    }
    return result;
  };

  getTableActionFieldsByKuikaId = (kuikaId: string, isNestedTable: boolean = false): IKTypeField[] | undefined => {
    if (!this.getScreenDesignContext()) {
      return;
    }

    const dict = this.getScreenDesignContext().screenDesignDictionary;
    const di = dict[parseInt(kuikaId, 10)];

    if (!di) {
      return undefined;
    }

    const tableOptionsPropSymbol: ISymbol | undefined = this.getTableOptionPropSymbolByDi(di);

    if (!tableOptionsPropSymbol) {
      return;
    }

    let elements: IKTypeField[] = this.getActionFieldsBySymbol(tableOptionsPropSymbol, isNestedTable);

    if (tableOptionsPropSymbol && tableOptionsPropSymbol.actionDefinitionId) {
      const action: IAction | undefined = this.getActionDynamicFields(tableOptionsPropSymbol.actionDefinitionId);

      if (action?.returnTypeDynamicInterface) {
        action.returnTypeDynamicInterface.elements?.forEach((element: IKTypeField) => {
          if (!elements.some((e) => e.name === element.name)) elements.push(_.clone(element));
        });
      }
    }

    if (isNestedTable) {
      return elements.filter((e) => e.isList === true && e.elementTypeModel != null);
    }

    return elements.filter((e) => e.isList !== true && e.elementTypeModel == null);
  };

  getParent = (di: IDisplayItem): IDisplayItem | undefined => {
    const dict = this.getScreenDesignContext().screenDesignDictionary;

    if (!di?.parentId) {
      return undefined;
    }

    const parentDi = dict[di.parentId];
    return parentDi;
  };

  getActionDynamicFields = (actionDefinitionId: string): IAction | undefined => {
    const sd: IScreenDesign = this.getScreenDesignContext().getScreenDesign();

    if (!sd) {
      return;
    }

    const actions = this.findActionsInRecursiveDislpayItems(sd.displayItems, []);
    return actions.find((a) => a.actionDefinitionId === actionDefinitionId);
  };

  private findActionsInRecursiveDislpayItems = (dis: IDisplayItem[], actions: IAction[]): IAction[] => {
    const actionList = actions;
    dis.forEach((di) => {
      if (di.item && di.item.events) {
        di.item.events.forEach((event) => {
          event.actions.forEach((action) => {
            actionList.push(action);
          });
        });
      }

      if (di.children) {
        this.findActionsInRecursiveDislpayItems(di.children, actionList);
      }
    });
    return actionList;
  };

  getTableOptionPropSymbolByDi = (di: IDisplayItem): ISymbol | undefined => {
    const sd: IScreenDesign = this.getScreenDesignContext().getScreenDesign();

    if (!sd) {
      return;
    }

    if (!di || !di.item) {
      return;
    }

    if (di?.id === SpecialDIIds.rootDI) {
      return;
    }

    let diParent: IDisplayItem | undefined = this.getParent(di);

    if (!diParent?.item) {
      return;
    }

    let parentdiComp = diParent.item;
    let { item } = diParent;

    // Should skip KanbanBoardLane element
    if (item.name === "KanbanBoardLane") {
      diParent = this.getParent(diParent) as IDisplayItem;
      parentdiComp = diParent.item;
      item = { ...diParent.item };
      if (!item) return;
    }

    const result: IDisplayItemProperty | undefined = parentdiComp.properties.find((p: IDisplayItemProperty) => {
      const propertyDef = this.getScreenDesignContext().getPropertyDefinition(item, p);
      return (
        propertyDef?.isTableOptionsProp === true ||
        (propertyDef?.isGoogleMapsOptionsProp === true && propertyDef.name !== "waypoints") ||
        (di.item.name === "KanbanBoardCard" && propertyDef?.isKanbanBoardCardOptionsProp === true) ||
        (di.item.name === "KanbanBoardLaneHeader" && propertyDef?.isKanbanBoardLaneOptionsProp === true) ||
        (di.item.name === "NodeTree" && propertyDef?.isNodeTreeNodeOptionsProp === true)
      );
    });

    if (result) {
      return result.symbol;
    }

    return this.getTableOptionPropSymbolByDi(diParent);
  };

  getComponentName = (kuikaId: string) => {
    if (!this.getScreenDesignContext()) {
      return "";
    }
    const sd = this.getScreenDesignContext().getScreenDesign();
    const dict = this.getScreenDesignContext().screenDesignDictionary;

    const di = dict[parseInt(kuikaId, 10)];
    if (!di || !di.item) {
      return "";
    }
    if (di?.id === SpecialDIIds.rootDI) {
      return "Screen";
    }
    let selectedComponentName = "";
    const currentDIComponent = di?.item;
    if (currentDIComponent) {
      const iCompDef = this.getScreenDesignContext().getComponentDefinition(currentDIComponent) as IComponent;
      selectedComponentName = iCompDef?.name;
    }
    return selectedComponentName;
  };

  /**
   * KOA-18226 — reports WHERE the action was routed and whether it actually landed.
   *
   * This returned `void`, and several of the branches below are empty: with a stale
   * `dashboardState` and no selected sub-entity, a screen action-attach was dropped on
   * the floor. `item?.events?.push(...)` does the same silently when the component is
   * not in the dictionary this reads. The MCP attach handler then reported
   * `success: true` regardless, because there was nothing to check — so a screen could
   * finish "wired" with an empty event list, the Binder would fail to bind onto an event
   * that was never created, and the retry/replan machinery would spend a whole build
   * chasing a plan that was never wrong (live repro `chat_1787691143910_9s8aur`).
   *
   * The verdict is READ BACK from the component rather than inferred from the branch, so
   * it reports what is true rather than what was attempted.
   */
  saveActionToEvent = (
    kuikaId: string,
    action: IAction,
    event: IEvent,
    changeReason?: ESdChangeReason
  ): SaveActionToEventResult => {
    const sd = this.getScreenDesignContext().getScreenDesign();
    const dict = this.getScreenDesignContext().screenDesignDictionary;
    const sJob = this.getScreenDesignContext().getSelectedSj();
    const api = this.getScreenDesignContext().getSelectedApi();
    const apiMethod = this.getScreenDesignContext().getSelectedApiMethod();
    const wf = this.getScreenDesignContext().selectedWf;
    const wfStep = this.getScreenDesignContext().getSelectedWfStep();
    const bleMethod = this.getScreenDesignContext().getSelectedBluetoothMethod();
    const ds = this.getScreenDesignContext().activeEditingDatasource;
    // const project = this.getScreenDesignContext().project;

    const { dashboardState } = this.getScreenDesignContext();
    const di = dict[parseInt(kuikaId, 10)];
    const item = di?.item;
    let owner: SaveActionOwner;
    if (this.getScreenDesignContext().isDocumentParameterAuthoringActive()) {
      // The tool is its own host: no selected sub-entity to guard on, unlike scheduled jobs or api methods.
      //
      // Asked of the authoring context rather than the dashboard state, and this branch stays first, because the
      // popup form of the tool runs over the design canvas with a Document component selected. Keyed on
      // `dashboardState` it would have fallen through to the display-item branch and written a source action onto
      // that component's own event — a silent write to the wrong owner, on the screen the user was editing.
      this.getScreenDesignContext().addActionToDocumentParameters(action);
      owner = "document-parameters";
    } else if (sJob && dashboardState === DashboardState.scheduledJobs) {
      this.getScreenDesignContext().addActionToScheduledJob(action, sJob, true);
      owner = "scheduled-job";
    } else if (sJob === undefined && dashboardState === DashboardState.scheduledJobs) {
      owner = "dropped-scheduled-jobs-no-job";
    } else if (api && apiMethod && dashboardState === DashboardState.apiManagement) {
      this.getScreenDesignContext().addActionToMethod(action);
      owner = "api-method";
    } else if ((api === undefined || apiMethod === undefined) && dashboardState === DashboardState.apiManagement) {
      owner = "dropped-api-no-method";
    } else if (wf && wfStep && dashboardState === DashboardState.workFlow) {
      this.getScreenDesignContext().addActionToWorkflowStep(action, changeReason);
      owner = "workflow-step";
    } else if ((wf === undefined || wfStep === undefined) && dashboardState === DashboardState.workFlow) {
      owner = "dropped-workflow-no-step";
    } else if (
      ds?.datasourceType === EDatasourceType.BleService &&
      bleMethod &&
      dashboardState === DashboardState.dataDiagramScheme
    ) {
      this.getScreenDesignContext().addActionToBluetoothMethod(action);
      owner = "bluetooth-method";
    } else if (
      (ds?.datasourceType !== EDatasourceType.BleService || !bleMethod) &&
      dashboardState === DashboardState.dataDiagramScheme
    ) {
      owner = "dropped-data-diagram-no-method";
    } else {
      owner = item === undefined ? "dropped-component-missing" : "component";
      if (item !== undefined && !item.events) {
        item.events = [];
      }
      let evt = item?.events?.find((e) => e.eventName == event.eventName);
      if (!evt) {
        item?.events?.push(event);
        evt = event;
      }
      const sdContext = this.getScreenDesignContext();
      const currentActionIndex = evt?.actions.findIndex((a) => a.id == action.id);
      // Capture the action being replaced BEFORE the swap so the StartProcess diff
      // below can see its previous target workflow (edit / process-change case).
      const oldAction = currentActionIndex > -1 ? evt.actions[currentActionIndex] : undefined;
      if (currentActionIndex > -1) {
        evt.actions[currentActionIndex] = action;
      } else {
        evt.actions.push(action);
      }
      if (action.label === "SendMail") {
        (action as any).isNewTemplate = true;
      }
      sdContext.setScreenDesign(sd, changeReason !== undefined ? changeReason : ESdChangeReason.DiActionUpdated);
      // Central StartProcess trigger sync for SCREEN create/edit/paste. Every UI and
      // MCP screen action save funnels through here, so the reverse index stays
      // consistent without scattering hooks at each call site. snapshot() resolves
      // StartProcess actions authoritatively via actionDefinitionId (→ definition name)
      // and returns [] for anything else, so unrelated saves no-op out below.
      const triggerSync = this.sdInstance?.startProcessTriggerSync;
      if (triggerSync) {
        const previousSnapshot = triggerSync.snapshot(oldAction ? [oldAction] : []);
        const currentSnapshot = triggerSync.snapshot([action]);
        if (previousSnapshot.length > 0 || currentSnapshot.length > 0) {
          triggerSync.applyDiff({
            sourceType: "Screen",
            sourceId: sd?.id,
            previousSnapshot,
            currentSnapshot
          });
        }
      }
    }
    // Read the verdict back off the component instead of trusting the branch. A write
    // routed anywhere but `component` never touched this event, and the component path
    // itself no-ops when the item is missing.
    const landedEvent: IEvent | undefined = item?.events?.find((e) => e.eventName === event.eventName);
    const attachedToComponent: boolean =
      owner === "component" && (landedEvent?.actions ?? []).some((a) => a.id === action.id);
    return { owner, attachedToComponent, dashboardState };
  };

  deleteActionFromEvent = (kuikaId: string, action: IAction, event: IEvent) => {
    const sd = this.getScreenDesignContext().getScreenDesign();
    const dict = this.getScreenDesignContext().screenDesignDictionary;
    // const di = ScreenDesignHelper.findDisplayItemBykuikaid(kuikaId, sd.displayItems);
    const di = dict[parseInt(kuikaId, 10)];
    const item = di?.item;
    if (action.label === "NAVIGATE" || action.label === "NavigateToTask") {
      const navKuikaIdParam = action.parameters.find(
        (x) => x.name === "navigationKuikaId" && x.symbol.fixedValue && x.symbol.fixedValue !== ""
      );
      if (navKuikaIdParam) {
        this.getScreenDesignContext().deleteItemByKuikaId(navKuikaIdParam.symbol.fixedValue, kuikaId);
      }
    }
    if (item !== undefined && !item.events) {
      item.events = [];
    }
    const evt = item?.events?.find((e) => e.eventName === event.eventName);
    const triggerSync = this.sdInstance?.startProcessTriggerSync;
    if (evt?.actions) {
      evt.actions = evt?.actions.filter((x) => x.id !== action.id);
    }
    const sdContext = this.getScreenDesignContext();
    sdContext.setScreenDesign(sd, ESdChangeReason.DiActionUpdated);
    if (!triggerSync) return;
    // Snapshot the deleted action's subtree; isStartProcessAction resolves it
    // authoritatively via actionDefinitionId, so a non-StartProcess action yields an
    // empty snapshot and the index update is skipped.
    const removedSnapshot = triggerSync.snapshot([action]);
    if (removedSnapshot.length === 0) return;
    triggerSync.applyDiff({
      sourceType: "Screen",
      sourceId: sd?.id,
      previousSnapshot: removedSnapshot,
      currentSnapshot: []
    });
  };

  saveValidationToIValidation = (
    kuikaId: string,
    validation: IValidation,
    changeReason = ESdChangeReason.UpdateValidation
  ) => {
    const sd = this.getScreenDesignContext().getScreenDesign();
    const dict = this.getScreenDesignContext().screenDesignDictionary;
    const di = dict[parseInt(kuikaId, 10)];
    const item = di?.item;
    if (item !== undefined && !item.validations) {
      item.validations = [];
    }
    const currentValidation = item?.validations?.find((e) => e.id == validation.id);
    if (!currentValidation) {
      item?.validations?.push(validation);
    } else {
      currentValidation.condition = validation.condition;
      currentValidation.message = validation.message;
      currentValidation.isExpandedPanel = validation.isExpandedPanel;
      currentValidation.validationCondition = validation.validationCondition;
    }
    const sdContext = this.getScreenDesignContext();
    sdContext.setScreenDesign(sd, changeReason);
  };

  saveVisibilitytoDisplayItem = (
    kuikaId: string,
    visibility: IVisibility,
    changeReason = ESdChangeReason.UpdateVisibilty
  ) => {
    const sdContext = this.getScreenDesignContext();
    const sd = sdContext.getScreenDesign();
    const dict = sdContext.screenDesignDictionary;
    // const di = ScreenDesignHelper.findDisplayItemBykuikaid(kuikaId, sd.displayItems);
    const di = dict[parseInt(kuikaId, 10)];

    const item = di?.item;
    if (item !== undefined) {
      item.visibility = visibility;
    }
    sdContext.setScreenDesign(sd, changeReason);
  };

  saveEditabilityToDisplayItem = (
    kuikaId: string,
    editability: IEditability,
    changeReason = ESdChangeReason.UpdateEditability
  ) => {
    const sdContext = this.getScreenDesignContext();
    const sd = sdContext.getScreenDesign();
    const dict = sdContext.screenDesignDictionary;
    const di = dict[parseInt(kuikaId, 10)];

    if (di) {
      di.editability = editability;
    }

    sdContext.setScreenDesign(sd, changeReason);
  };

  deleteValidationToIValidation = (kuikaId: string, validation: IValidation) => {
    const sd = this.getScreenDesignContext().getScreenDesign();
    const dict = this.getScreenDesignContext().screenDesignDictionary;
    const di = dict[parseInt(kuikaId, 10)];
    const item = di?.item;
    if (item?.validations) {
      item.validations = item?.validations?.filter((e) => e.id !== validation.id);
      const sdContext = this.getScreenDesignContext();
      sdContext.setScreenDesign(sd, ESdChangeReason.UpdateValidation);
    }
  };

  addEventToDi = (kuikaId: string, event: IEvent) => {
    const sd = this.getScreenDesignContext().getScreenDesign();
    const dict = this.getScreenDesignContext().screenDesignDictionary;
    // const di = ScreenDesignHelper.findDisplayItemBykuikaid(kuikaId, sd.displayItems);
    const di = dict[parseInt(kuikaId, 10)];
    const item = di?.item;
    if (item !== undefined && !item.events) {
      item.events = [];
    }
    const evt = item?.events?.find((e) => e.eventName == event.eventName);
    if (!evt) {
      item?.events?.push(event);
      const sdContext = this.getScreenDesignContext();
      sdContext.setScreenDesign(sd, ESdChangeReason.DiActionUpdated);
    }
  };

  applyPropertyGridChangesToDisplay(
    params: IApplyPropertyGridChangesToDisplayParameters,
    changeReason: ESdChangeReason,
    shouldCommit: boolean = true
  ): boolean {
    if (!this.getScreenDesignContext() || !params.kuikaId) {
      return false;
    }
    const sdCtx = this.getScreenDesignContext();
    const sd = sdCtx.getScreenDesign();
    const dict = sdCtx.screenDesignDictionary;
    // let di = ScreenDesignHelper.findDisplayItemBykuikaid(params.kuikaId, sd.displayItems);
    let di = dict[parseInt(params.kuikaId, 10)];

    if (!di || !di.item) {
      return false;
    }
    const currentDIComponent = di.item;
    const componentDefinition = this.getScreenDesignContext().getComponentDefinition(currentDIComponent) as
      | IComponent
      | undefined;
    const selectedComponentName: string | undefined = this.getComponentName(params.kuikaId);
    if (selectedComponentName === undefined) {
      return false;
    }

    let isChanged = false;
    let needProjectUpdate = false;
    const { projectTempStyles } = sdCtx;
    let autoIncrementedStyleIndex: number = 0;
    if (di && di.styleObj && di.styleObj.autoIncrementedIndex) {
      autoIncrementedStyleIndex = di.styleObj.autoIncrementedIndex;
    }

    // Handle Label writingMode changes - swap width and height when orientation changes
    if (selectedComponentName === "Label" && params.propertyName === "writingMode") {
      const oldWritingMode = di.item.properties.find((x) => x.name === "writingMode")?.symbol.fixedValue;
      const newWritingMode = params.symbol?.fixedValue || params.value;

      const isOldHorizontal = !oldWritingMode || oldWritingMode === "horizontal";
      const isOldVertical = oldWritingMode === "vertical-left" || oldWritingMode === "vertical-right";
      const isNewHorizontal = !newWritingMode || newWritingMode === "horizontal";
      const isNewVertical = newWritingMode === "vertical-left" || newWritingMode === "vertical-right";

      // If orientation is changing (horizontal <-> vertical), swap width and height
      if ((isOldHorizontal && isNewVertical) || (isOldVertical && isNewHorizontal)) {
        if (di.styleObj?.position) {
          const tempWidth = di.styleObj.position.width;
          const tempHeight = di.styleObj.position.height;

          if (tempWidth !== undefined && tempHeight !== undefined) {
            di.styleObj.position.width = tempHeight;
            di.styleObj.position.height = tempWidth;

            // Also update absolutePositionInfo and fixedPositionInfo if they exist
            if (di.absolutePositionInfo) {
              const tempAbsWidth = di.absolutePositionInfo.width;
              const tempAbsHeight = di.absolutePositionInfo.height;
              if (tempAbsWidth !== undefined && tempAbsHeight !== undefined) {
                di.absolutePositionInfo.width = tempAbsHeight;
                di.absolutePositionInfo.height = tempAbsWidth;
              }
            }

            if (di.fixedPositionInfo) {
              const tempFixedWidth = di.fixedPositionInfo.width;
              const tempFixedHeight = di.fixedPositionInfo.height;
              if (tempFixedWidth !== undefined && tempFixedHeight !== undefined) {
                di.fixedPositionInfo.width = tempFixedHeight;
                di.fixedPositionInfo.height = tempFixedWidth;
              }
            }

            needProjectUpdate = true;
          }
        }
      }
    }
    if (params.propertyName === "styleObj") {
      isChanged = true;
      const newStyleObj: IKuikaStyle | undefined = _.cloneDeep(params.value);

      // Block dimension resize on merged StaticTableColumn cells.
      // Merged cells derive their width/height from the base grid via recalculation.
      if (di.item?.name === "StaticTableColumn" && newStyleObj?.position) {
        const colspan = StaticTableHelper.getExistingSpanValue(di, "colspan");
        const rowspan = StaticTableHelper.getExistingSpanValue(di, "rowspan");
        if (colspan > 1) {
          // Preserve existing width — don't allow direct resize
          newStyleObj.position.width = di.styleObj?.position?.width;
          newStyleObj.position.widthStyle = di.styleObj?.position?.widthStyle;
        }
        if (rowspan > 1) {
          // Preserve existing height — don't allow direct resize
          newStyleObj.position.height = di.styleObj?.position?.height;
          newStyleObj.position.heightStyle = di.styleObj?.position?.heightStyle;
        }
      }

      di.styleObj = _.cloneDeep(newStyleObj);

      if (di.styleObj && di.styleObj.text && newStyleObj?.text?.typoId && newStyleObj?.text?.typoId !== "-1") {
        projectTempStyles.tempStylingTypography = {
          ...newStyleObj?.text,
          kuikaid: di.id.toString()
        } as IStylingTypography;
      } else {
        projectTempStyles.tempStylingTypography = undefined;
      }

      if (
        di.styleObj &&
        di.styleObj.border &&
        newStyleObj?.border?.borderId &&
        newStyleObj?.border?.borderId !== "-1"
      ) {
        projectTempStyles.tempStylingBorder = {
          ...newStyleObj?.border,
          kuikaid: di.id.toString()
        } as IStylingBorder;
      } else {
        projectTempStyles.tempStylingBorder = undefined;
      }

      if (
        di.styleObj &&
        di.styleObj.shadow &&
        newStyleObj?.shadow?.shadowId &&
        newStyleObj?.shadow?.shadowId !== "-1"
      ) {
        projectTempStyles.tempStylingShadow = {
          ...newStyleObj?.shadow,
          kuikaid: di.id.toString()
        } as IStylingShadow;
      } else {
        projectTempStyles.tempStylingShadow = undefined;
      }
      if (di.styleObj && di.styleObj.position && newStyleObj?.position?.positionId) {
        projectTempStyles.tempStylingPosition = {
          ...newStyleObj?.position,
          kuikaid: di.id.toString()
        } as IStylingPosition;
      } else {
        projectTempStyles.tempStylingPosition = undefined;
      }
      if (di.styleObj && di.styleObj.fill && newStyleObj?.fill?.fillId) {
        projectTempStyles.tempStylingFill = {
          ...newStyleObj?.fill,
          kuikaid: di.id.toString()
        } as IFillStyle;
      } else {
        projectTempStyles.tempStylingFill = undefined;
      }

      if (
        di.styleObj &&
        di.styleObj.effects &&
        newStyleObj?.effects?.effectsId &&
        newStyleObj?.effects?.effectsId !== "-1"
      ) {
        projectTempStyles.tempStylingEffects = {
          ...newStyleObj?.effects,
          kuikaid: di.id.toString()
        } as IStylingEffects;
      } else {
        projectTempStyles.tempStylingEffects = undefined;
      }

      if (di && di.absolutePositionInfo && di.fixedPositionInfo) {
        if (newStyleObj?.position?.width) {
          di.absolutePositionInfo.width = newStyleObj?.position?.width;
          di.fixedPositionInfo.width = newStyleObj?.position?.width;
        } else {
          di.absolutePositionInfo.width = undefined;
          di.fixedPositionInfo.width = undefined;
        }
        if (newStyleObj?.position?.height) {
          if (di && di.parentId && di.fixedPositionInfo.height && newStyleObj?.position?.yAxis) {
            let diFixedTop = di.fixedPositionInfo.top ?? 0;
            const heightDiff = newStyleObj?.position?.height - di.fixedPositionInfo.height;
            let yAxisDiff = newStyleObj?.position?.yAxis - diFixedTop;
            if (diFixedTop === 0) yAxisDiff = yAxisDiff / 2;
            // const parentDi = ScreenDesignHelper.findDisplayItemBykuikaid(di.parentId.toString(), sd.displayItems);
            const parentDi = dict[di.parentId];
            if (parentDi?.styleObj?.position?.height && newStyleObj.position.height && newStyleObj.position.yAxis) {
              const isElementHeightHigherThanContainer =
                parentDi.styleObj.position.height < newStyleObj.position.height + newStyleObj.position.yAxis;
              if (
                parentDi &&
                isElementHeightHigherThanContainer &&
                parentDi.absolutePositionInfo?.height &&
                parentDi.fixedPositionInfo?.height &&
                parentDi.styleObj?.position?.height &&
                heightDiff > 0
              ) {
                parentDi.absolutePositionInfo.height += heightDiff;
                parentDi.fixedPositionInfo.height += heightDiff;
                parentDi.styleObj.position.height += heightDiff;
              }
              if (
                parentDi &&
                isElementHeightHigherThanContainer &&
                parentDi.absolutePositionInfo?.height &&
                parentDi.fixedPositionInfo?.height &&
                parentDi.styleObj?.position?.height &&
                yAxisDiff > 0
              ) {
                parentDi.absolutePositionInfo.height += yAxisDiff;
                parentDi.fixedPositionInfo.height += yAxisDiff;
                parentDi.styleObj.position.height += yAxisDiff;
              }
            }
          }
          di.absolutePositionInfo.height = newStyleObj?.position?.height;
          di.fixedPositionInfo.height = newStyleObj?.position?.height;
        } else {
          di.absolutePositionInfo.height = undefined;
          di.fixedPositionInfo.height = undefined;
        }
        if (di?.item?.name === "Panel") {
          const children = di.children;
          if (children && children.length > 0) {
            const childrenBottoms: number[] = [];
            children.forEach((child) => {
              if (child.styleObj?.position?.yAxis && child.styleObj?.position?.height) {
                childrenBottoms.push(child.styleObj.position.yAxis + child.styleObj.position.height);
              }
            });
            const nearestBottom = Math.max(...childrenBottoms);
            const newStyleObjHeight = newStyleObj?.position?.height ?? 0;
            if (nearestBottom > 0 && nearestBottom > newStyleObjHeight) {
              di.absolutePositionInfo.height = nearestBottom + 10;
              di.fixedPositionInfo.height = nearestBottom + 10;
              if (di.styleObj?.position) di.styleObj.position.height = nearestBottom + 10;
            }
          }
        }
        if (newStyleObj?.position?.xAxis !== undefined) {
          di.absolutePositionInfo.left = newStyleObj?.position?.xAxis;
          di.fixedPositionInfo.left = newStyleObj?.position?.xAxis;
        }
        if (newStyleObj?.position?.yAxis !== undefined) {
          di.absolutePositionInfo.top = newStyleObj?.position?.yAxis;
          di.fixedPositionInfo.top = newStyleObj?.position?.yAxis;
        }
      }

      // Two-phase StaticTable dimension sync:
      // Phase 1: Sync non-merged cells (base grid)
      // Phase 2: Recalculate all merged cells from the base grid
      if (di.item?.name === "StaticTableColumn" && di.parentId) {
        const parentRowDi = dict[di.parentId];
        const staticTableDi = parentRowDi ? ScreenDesignHelper.findParentTable(parentRowDi, dict) : undefined;

        // Phase 1a: Height — sync rowspan=1 siblings in the same row
        if (newStyleObj?.position?.height && parentRowDi?.children) {
          const sourceRowspan = StaticTableHelper.getExistingSpanValue(di, "rowspan");
          if (sourceRowspan === 1) {
            parentRowDi.children.forEach((siblingColumn) => {
              if (
                siblingColumn.id !== di.id &&
                StaticTableHelper.getExistingSpanValue(siblingColumn, "rowspan") === 1
              ) {
                if (!siblingColumn.styleObj) siblingColumn.styleObj = {} as IKuikaStyle;
                if (!siblingColumn.styleObj.position) siblingColumn.styleObj.position = {} as IStylingPosition;
                siblingColumn.styleObj.position.height = newStyleObj.position!.height;
                siblingColumn.styleObj.position.heightStyle = newStyleObj.position!.heightStyle || "px";
                if (siblingColumn.absolutePositionInfo)
                  siblingColumn.absolutePositionInfo.height = newStyleObj.position!.height;
                if (siblingColumn.fixedPositionInfo)
                  siblingColumn.fixedPositionInfo.height = newStyleObj.position!.height;
              }
            });
          }
        }

        // Phase 1b: Width — sync colspan=1 cells at the same logical column in all rows
        if (newStyleObj?.position?.width && parentRowDi?.children) {
          const sourceColspan = StaticTableHelper.getExistingSpanValue(di, "colspan");
          if (sourceColspan === 1) {
            const physicalIndex = parentRowDi.children.findIndex((col) => col.id === di.id);
            if (physicalIndex >= 0) {
              const logicalIndex = StaticTableHelper.getLogicalColumnIndex(parentRowDi, physicalIndex);
              if (staticTableDi?.children) {
                staticTableDi.children.forEach((rowDi) => {
                  if (rowDi.item?.name === "StaticTableRow") {
                    const targetResult = StaticTableHelper.findPhysicalColumnByLogicalIndex(rowDi, logicalIndex);
                    if (targetResult && targetResult.column.id !== di.id) {
                      const targetColspan = targetResult.logicalEnd - targetResult.logicalStart;
                      // Only sync to other colspan=1 cells at exact same logical position
                      if (targetColspan === 1 && targetResult.logicalStart === logicalIndex) {
                        const targetColumn = targetResult.column;
                        if (!targetColumn.styleObj) targetColumn.styleObj = {} as IKuikaStyle;
                        if (!targetColumn.styleObj.position) targetColumn.styleObj.position = {} as IStylingPosition;
                        targetColumn.styleObj.position.width = newStyleObj.position!.width;
                        targetColumn.styleObj.position.widthStyle = newStyleObj.position!.widthStyle || "px";
                        if (targetColumn.absolutePositionInfo)
                          targetColumn.absolutePositionInfo.width = newStyleObj.position!.width;
                        if (targetColumn.fixedPositionInfo)
                          targetColumn.fixedPositionInfo.width = newStyleObj.position!.width;
                      }
                    }
                  }
                });
              }
            }
          }
        }

        // Phase 2: Recalculate all merged cells from the base grid
        if (staticTableDi && (newStyleObj?.position?.width || newStyleObj?.position?.height)) {
          StaticTableHelper.recalculateAllMergedCellDimensions(staticTableDi, dict);
        }
      }

      // Height sync for ReportTableColumn (non-StaticTable, no merge logic needed)
      if (di.item?.name === "ReportTableColumn" && di.parentId && newStyleObj?.position?.height) {
        const parentRowDi = dict[di.parentId];
        if (parentRowDi?.children) {
          parentRowDi.children.forEach((siblingColumn) => {
            if (siblingColumn.id !== di.id) {
              if (!siblingColumn.styleObj) siblingColumn.styleObj = {} as IKuikaStyle;
              if (!siblingColumn.styleObj.position) siblingColumn.styleObj.position = {} as IStylingPosition;
              siblingColumn.styleObj.position.height = newStyleObj.position!.height;
              siblingColumn.styleObj.position.heightStyle = newStyleObj.position!.heightStyle || "px";
              if (siblingColumn.absolutePositionInfo)
                siblingColumn.absolutePositionInfo.height = newStyleObj.position!.height;
              if (siblingColumn.fixedPositionInfo)
                siblingColumn.fixedPositionInfo.height = newStyleObj.position!.height;
            }
          });
        }
      }

      // The backup* snapshots belong to the styling panel's own tag-edit flow. A write coming from outside
      // the panel (the canvas selection bar) must land as given: applying the panel's snapshot here would
      // silently replace the value the user just set on the canvas.
      const isExternalStyleWrite: boolean = changeReason === ESdChangeReason.ChangeStyleValueExternal;
      const stylingPositionBackup: IStylingPosition | undefined = isExternalStyleWrite
        ? undefined
        : sdCtx.backupCustomPosition;
      const customStylingTypographyBackup: IStylingTypography | undefined = isExternalStyleWrite
        ? undefined
        : sdCtx.backupCustomTypography;
      const customStylingBorderBackup: IStylingBorder | undefined = isExternalStyleWrite
        ? undefined
        : sdCtx.backupCustomBorder;
      const customStylingShadowBackup: IStylingShadow | undefined = isExternalStyleWrite
        ? undefined
        : sdCtx.backupCustomShadow;
      const customStylingFillBackup: IFillStyle | undefined = isExternalStyleWrite ? undefined : sdCtx.backupCustomFill;
      const customStylingEffectsBackup: IStylingEffects | undefined = isExternalStyleWrite
        ? undefined
        : sdCtx.backupCustomEffects;
      di.styleObj = StyleHelper.convertFormStyleToDiStyle(
        di.id.toString(),
        di.styleObj,
        stylingPositionBackup,
        customStylingTypographyBackup,
        customStylingBorderBackup,
        customStylingShadowBackup,
        customStylingFillBackup,
        customStylingEffectsBackup
      );
      if (di.styleObj) {
        di.styleObj.autoIncrementedIndex = autoIncrementedStyleIndex + 1;
      }
      needProjectUpdate = true;
    }
    let property: IDisplayItemProperty | undefined;
    property = di.item.properties.find((x) => x.name === params.propertyName);
    if (property) {
      if (params.symbol) {
        const propertyIndex = di.item.properties.findIndex((x) => x.name === params.propertyName);
        if (propertyIndex !== -1) {
          const nextSymbol = _.cloneDeep(params.symbol);
          if (!_.isEqual(property.symbol, nextSymbol)) {
            const updatedProperty = _.cloneDeep(property);
            updatedProperty.symbol = nextSymbol;
            di.item.properties[propertyIndex] = updatedProperty;
            isChanged = true;
          }
        }
      } else if (property.symbol.fixedValue !== params.value) {
        const propertyIndex = di.item.properties.findIndex((x) => x.name === params.propertyName);
        if (propertyIndex !== -1) {
          const updatedProperty = _.cloneDeep(property);
          updatedProperty.symbol = {
            ...updatedProperty.symbol,
            category: ESymbolCategory.fixed,
            fixedValue: params.value
          };
          di.item.properties[propertyIndex] = updatedProperty;
          isChanged = true;
        }
      }
    } else if (params.symbol) {
      // If the property doesn't exist yet and a symbol is provided, create the property dynamically
      const newProperty: IDisplayItemProperty = {
        name: params.propertyName,
        symbol: _.cloneDeep(params.symbol),
        selectedType: 1
      } as IDisplayItemProperty;
      di.item.properties.push(newProperty);
      isChanged = true;
    } else {
      const propertyDefinition = componentDefinition?.properties?.find((x) => x.name === params.propertyName);
      if (propertyDefinition) {
        const newProperty: IDisplayItemProperty = {
          name: params.propertyName,
          symbol: {
            category: ESymbolCategory.fixed,
            fixedValue: params.value
          } as ISymbol,
          selectedType: propertyDefinition.selectedType ?? propertyDefinition.type?.[0] ?? EPropertyType.string,
          selectedTypeOptions: propertyDefinition.typeOptions
        } as unknown as IDisplayItemProperty;
        di.item.properties.push(newProperty);
        isChanged = true;
      }
    }

    const isParentHeaderOrFooter = ScreenDesignHelper.isParentHeaderOrFooter(di, sd.displayItems);
    const isParentLeftOrRightLayout = ScreenDesignHelper.isParentLeftOrRightLayout(di, sd.displayItems);
    if (di && di.item?.name === "Menu") {
      let mode = di.item.properties.find((x) => x.name === "mode");
      if (mode && isParentHeaderOrFooter) {
        mode.symbol.fixedValue = "horizontal";
      } else if (mode && isParentLeftOrRightLayout) {
        mode.symbol.fixedValue = "vertical";
      }
    }
    if (sd && isChanged === true) {
      if (needProjectUpdate === true) {
        sdCtx.setProjectTempStyles(projectTempStyles);
      }
      if (shouldCommit) {
        const sdContext = this.getScreenDesignContext();
        sdContext.setScreenDesign(sd, changeReason);
      }
    }

    return isChanged;
  }

  getProperties = (kuikaId: string): IProperty[] => {
    let propertyList: IProperty[] = [];
    const dict = this.getScreenDesignContext().screenDesignDictionary;
    // const di = ScreenDesignHelper.findDisplayItemBykuikaid(kuikaId, sd.displayItems);
    const di = dict[parseInt(kuikaId, 10)];

    propertyList = this.getPropertiesByDisplayItem(di);
    return propertyList;
  };

  getPropertiesByDisplayItem = (di: IDisplayItem | undefined): IProperty[] => {
    const propertyList: IProperty[] = [];
    const component = di?.item;
    let propertyDefinition: IPropertyDefinition | undefined;

    // reorder properties
    const originalComponents = this.getScreenDesignContext().project.componentSetObj.components;
    const originalComponent = originalComponents.find((x) => x.id === component?.id);
    if (originalComponent && component !== undefined) {
      originalComponent.properties.forEach((p, idx) => {
        if (p.name !== component?.properties[idx]?.name) {
          const propertyToMove = component?.properties.find((x) => x.name === p.name);
          if (propertyToMove) {
            const fromIdx = component?.properties.indexOf(propertyToMove);
            if (fromIdx !== undefined) {
              component?.properties.splice(idx, 0, component?.properties?.splice(fromIdx, 1)[0]);
            }
          }
        }
      });
    }
    // end of reorder properties

    component?.properties?.forEach((displayItemProperty) => {
      if (component !== undefined) {
        // Check for dynamic properties FIRST - they have their own definition
        if (displayItemProperty.isDynamicProperty) {
          const dynamicDef = displayItemProperty.dynamicPropertyDefinition;
          const property: IProperty = {
            description: dynamicDef?.description ?? "",
            hasOptions: dynamicDef?.hasOptions ?? false,
            isRequired: dynamicDef?.isRequired ?? false,
            isTechnical: dynamicDef?.isTechnical ?? false,
            isVisibleInFieldPicker: dynamicDef?.isVisibleInFieldPicker ?? false,
            isValueProp: dynamicDef?.isValueProp ?? false,
            isOptionsProp: dynamicDef?.isOptionsProp ?? false,
            isLanguageOptionsProp: dynamicDef?.isLanguageOptionsProp ?? false,
            isTableOptionsProp: dynamicDef?.isTableOptionsProp ?? false,
            isGoogleMapsOptionsProp: dynamicDef?.isGoogleMapsOptionsProp ?? false,
            isKanbanBoardLaneOptionsProp: dynamicDef?.isKanbanBoardLaneOptionsProp ?? false,
            isKanbanBoardCardOptionsProp: dynamicDef?.isKanbanBoardCardOptionsProp ?? false,
            isNodeTreeNodeOptionsProp: dynamicDef?.isNodeTreeNodeOptionsProp ?? false,
            isEditabilityProp: dynamicDef?.isEditabilityProp ?? false,
            isVisibleProp: dynamicDef?.isVisibleProp ?? false,
            isAdvanced: dynamicDef?.isAdvanced ?? false,
            isIgnoredOnCodeOutput: dynamicDef?.isIgnoredOnCodeOutput ?? false,
            isIgnoredOnGeneratedApp: dynamicDef?.isIgnoredOnGeneratedApp ?? false,
            isSupportMultiLanguage: dynamicDef?.isSupportMultiLanguage ?? false,
            appSupportType: dynamicDef?.appSupportType ?? 0,
            isDeprecated: dynamicDef?.isDeprecated ?? false,
            name: dynamicDef?.name ?? displayItemProperty.name,
            options: dynamicDef?.options ?? [],
            targetComponentName: displayItemProperty.targetComponentName ?? "",
            targetComponentPropertyName: displayItemProperty.targetComponentPropertyName ?? "",
            targetKuikaId: displayItemProperty.targetKuikaId ?? "",
            selectedType: displayItemProperty.selectedType,
            selectedTypeOptions: displayItemProperty.selectedTypeOptions,
            tagName: dynamicDef?.tagName ?? "",
            type: dynamicDef?.type ?? [displayItemProperty.selectedType],
            typeOptions: dynamicDef?.typeOptions ?? [],
            symbol: displayItemProperty.symbol,
            defaultValue: dynamicDef?.defaultValue ?? "",
            kuikaTypeName: dynamicDef?.kuikaTypeName,
            isDomainType: dynamicDef?.isDomainType,
            domainTypeName: dynamicDef?.domainTypeName,
            isList: dynamicDef?.isList
          };
          propertyList.push(property);
        } else {
          // Regular properties - use property definition from component set
          propertyDefinition = this.getScreenDesignContext().getPropertyDefinition(component, displayItemProperty);
          if (propertyDefinition) {
            const property: IProperty = {
              description: propertyDefinition.description,
              hasOptions: propertyDefinition.hasOptions,
              isRequired: propertyDefinition.isRequired,
              isTechnical: propertyDefinition.isTechnical,
              isVisibleInFieldPicker: propertyDefinition.isVisibleInFieldPicker,
              isValueProp: propertyDefinition.isValueProp,
              isOptionsProp: propertyDefinition.isOptionsProp,
              isLanguageOptionsProp: propertyDefinition.isLanguageOptionsProp,
              isTableOptionsProp: propertyDefinition.isTableOptionsProp,
              isGoogleMapsOptionsProp: propertyDefinition.isGoogleMapsOptionsProp,
              isKanbanBoardLaneOptionsProp: propertyDefinition.isKanbanBoardLaneOptionsProp,
              isKanbanBoardCardOptionsProp: propertyDefinition.isKanbanBoardCardOptionsProp,
              isNodeTreeNodeOptionsProp: propertyDefinition.isNodeTreeNodeOptionsProp,
              isEditabilityProp: propertyDefinition.isEditabilityProp,
              isVisibleProp: propertyDefinition.isVisibleProp,
              isAdvanced: propertyDefinition.isAdvanced,
              isIgnoredOnCodeOutput: propertyDefinition.isIgnoredOnCodeOutput,
              isIgnoredOnGeneratedApp: propertyDefinition.isIgnoredOnGeneratedApp,
              isSupportMultiLanguage: propertyDefinition.isSupportMultiLanguage,
              appSupportType: propertyDefinition.appSupportType,
              isDeprecated: propertyDefinition.isDeprecated,
              name: propertyDefinition.name,
              options: propertyDefinition.options,
              targetComponentName: displayItemProperty.targetComponentName,
              targetComponentPropertyName: displayItemProperty.targetComponentPropertyName,
              targetKuikaId: displayItemProperty.targetKuikaId,
              selectedType: displayItemProperty.selectedType,
              selectedTypeOptions: displayItemProperty.selectedTypeOptions,
              tagName: propertyDefinition.tagName,
              type: propertyDefinition.type,
              typeOptions: propertyDefinition.typeOptions,
              symbol: displayItemProperty.symbol,
              defaultValue:
                displayItemProperty.symbol?.category === ESymbolCategory.fixed
                  ? displayItemProperty.symbol.fixedValue
                  : "",
              isChartOptionsProp: propertyDefinition.isChartOptionsProp,
              isTimelineOptionsProp: propertyDefinition.isTimelineOptionsProp,
              isCalendarOptionsProp: propertyDefinition.isCalendarOptionsProp,
              isMenuOptionsProp: propertyDefinition.isMenuOptionsProp,
              isBreadcrumbOptionsProp: propertyDefinition.isBreadcrumbOptionsProp,
              isRadialChartOptionsProp: propertyDefinition.isRadialChartOptionsProp,
              isRadialChartLevelOptionsProp: propertyDefinition.isRadialChartLevelOptionsProp,
              isMixedChartOptionsProp: propertyDefinition.isMixedChartOptionsProp,
              isMixedChartLineOptionsProp: propertyDefinition.isMixedChartLineOptionsProp,
              isMixedChartPointOptionsProp: propertyDefinition.isMixedChartPointOptionsProp,
              isMixedChartBarOptionsProp: propertyDefinition.isMixedChartBarOptionsProp,
              isChatOptionsProp: propertyDefinition.isChatOptionsProp,
              isTreeOptionsProp: propertyDefinition.isTreeOptionsProp,
              isDirectoryOptionsProp: propertyDefinition.isDirectoryOptionsProp,
              isSpecificDatesProp: propertyDefinition.isSpecificDatesProp,
              inVisibleStyles: propertyDefinition.inVisibleStyles,
              hideFixedValue: propertyDefinition.hideFixedValue,
              isTagsInput: propertyDefinition.isTagsInput
            };
            const componentProperty = component.properties.find((x) => x.name === property.name);
            // Deprecation is honoured from either side: the instance snapshot (older data) or the
            // component definition (a property retired after the instance was created). A
            // definition-deprecated prop the instance still BINDS stays visible, so an existing
            // binding can be seen and moved rather than silently kept alive.
            const symbolCategory = displayItemProperty.symbol?.category;
            const isEmptyFixedValue: boolean =
              symbolCategory === ESymbolCategory.fixed &&
              (displayItemProperty.symbol.fixedValue === undefined ||
                displayItemProperty.symbol.fixedValue === null ||
                displayItemProperty.symbol.fixedValue === "");
            const isBound: boolean =
              symbolCategory !== undefined && symbolCategory !== ESymbolCategory.none && !isEmptyFixedValue;
            const isDeprecated: boolean =
              componentProperty?.isDeprecated === true || (propertyDefinition.isDeprecated === true && !isBound);
            if (propertyDefinition.isTechnical !== true && !isDeprecated) {
              propertyList.push(property);
            }
          }
        }
      }
    });

    // KOA-13971: navigation components (Modal/Drawer/FloatPanel) are persisted in screen
    // trees as a property snapshot taken at creation time, so properties added to the
    // component definition later never surface for existing instances. Expose the missing
    // definition properties here (display-only merge) — editing one materializes it on the
    // instance via applyPropertyGridChangesToDisplay's create-if-missing branch.
    // KOA-18575: the same applies to VALUE props of any component — they are the component's
    // data contract (SetValueOf, validations and the code generator already read them from the
    // definition), so an instance that predates one (LocationSearch address/latitude/longitude)
    // must still be able to bind it.
    if (component !== undefined && originalComponent !== undefined) {
      const isNavigationComponent: boolean = originalComponent.isItNavigationComponent === true;
      originalComponent.properties.forEach((definitionProperty) => {
        if (definitionProperty.isTechnical === true || definitionProperty.isDeprecated === true) {
          return;
        }
        if (!isNavigationComponent && definitionProperty.isValueProp !== true) {
          return;
        }
        const existsOnInstance: boolean = component.properties.some((p) => p.name === definitionProperty.name);
        if (existsOnInstance) {
          return;
        }
        const mergedProperty: IProperty = {
          ..._.cloneDeep(definitionProperty),
          defaultValue:
            definitionProperty.symbol?.category === ESymbolCategory.fixed ? definitionProperty.symbol.fixedValue : ""
        };
        propertyList.push(mergedProperty);
      });
    }

    return propertyList;
  };

  getDefaultPropertiesByComponentId = (compId: string): IProperty[] => {
    const allComponents = this.getScreenDesignContext().project.componentSetObj.components;
    const comp = allComponents.find((x) => x.id === compId);
    if (!comp) return [];
    return _.cloneDeep(comp.properties);
  };

  isResponsiveColumnProperty = (property: IProperty): boolean => {
    const allComponents = this.getScreenDesignContext().project.componentSetObj.components;
    const responsiveColComponent = allComponents.find((c) => c.componentType === ComponentType.responsiveColumn);
    if (responsiveColComponent) {
      const responsiveColProperties = responsiveColComponent.properties.filter(
        (p) => p.type.indexOf(EPropertyType.column) !== -1
      );
      return responsiveColProperties.indexOf(property) !== -1;
    }
    return false;
  };

  isDIColComponent = (di: IDisplayItem): boolean => {
    const diId = di.item.id;
    const component = this.getScreenDesignContext().project.componentSetObj.components.find((x) => x.id === diId);
    if (component) {
      if (component.componentType === ComponentType.responsiveColumn) return true;
    }
    return false;
  };

  getParentRowDiWithColDI = (colDI: IDisplayItem): IDisplayItem | undefined => {
    if (!colDI.parentId) {
      return undefined;
    }

    const dict = this.getScreenDesignContext().screenDesignDictionary;
    return dict[colDI.parentId];
  };

  getParentRowId = (): string | undefined => {
    const sdContext = this.getScreenDesignContext();
    const sd = sdContext.getScreenDesign();
    const dict = sdContext.screenDesignDictionary;
    let selectedDisplayItem: IDisplayItem | undefined;

    if (KMainFunctions.isArrHasValue(sdContext.selectedKuikaIds)) {
      selectedDisplayItem = dict[parseInt(sdContext.selectedKuikaIds[0], 10)];
    }
    if (!selectedDisplayItem) {
      return undefined;
    }
    let rowDI: IDisplayItem | undefined;
    if (this.isDIColComponent(selectedDisplayItem)) {
      rowDI = this.getParentRowDiWithColDI(selectedDisplayItem);
    }
    if (!rowDI) {
      return undefined;
    }
    return rowDI.id.toString();
  };
}
